export default {
  signedOut: "You have signed out successfully.",
  session: {
    expired: "Session expired",
    expiredDescription: "You have been logged out due to inactivity"
  },
  signOutSuccess: "Sign out successful",
  signOutSuccessDescription: "You have been signed out successfully.",
  signOutFailed: "Sign out failed",
  signOutFailedDescription: "An error occurred during sign out.",
  signInSuccess: "Sign in successful",
  signInSuccessDescription: "You have been signed in successfully.",
  signInFailed: "Sign in failed",
  signInError: "There was an error signing in.",
  signOut: "Sign Out",
  showPassword: "Show password",
  hidePassword: "Hide password",
  noOrganization: {
    title: "No organization",
    description: "You are not part of any organization. Contact an administrator to receive an invitation.",
    signedInAs: "Signed in as"
  },
  login: {
    title: "Welcome to Prodgy",
    description: "Sign in to your account to continue",
    email: "Email",
    password: "Password",
    signIn: "Sign In",
    orContinueWith: "Or continue with",
    continueWithGoogle: "Continue with Google",
    continueWithMicrosoft: "Continue with Microsoft",
    forgotPassword: "Forgot password?",
    createAccount: "Create account",
    error: "Error",
    missingCredentials: "Please enter both email and password",
    poweredBy: "Powered by"
  },
  signup: {
    title: "Create your account",
    description: "Enter your email to get started with Prodgy",
    fields: {
      email: "Email",
      emailPlaceholder: "your@email.com"
    },
    info: {
      title: "Quick and easy setup",
      description: "We'll send you a confirmation link to complete your registration"
    },
    buttons: {
      continue: "Continue",
      backToLogin: "Back to login"
    },
    orContinueWith: "Or continue with",
    continueWithGoogle: "Continue with Google",
    continueWithMicrosoft: "Continue with Microsoft",
    validation: {
      emailRequired: "Email is required",
      emailInvalid: "Please enter a valid email address"
    },
    success: {
      title: "Check your email!",
      description: "We've sent you a confirmation link",
      message: "Please check your inbox and click the link to complete your registration. The link will expire in 24 hours."
    },
    errors: {
      title: "Error",
      generic: "An error occurred during sign up. Please try again.",
      userExists: "An account with this email already exists",
      userExistsInline: "*An account with this email address already exists",
      rateLimitExceeded: "Too many attempts. Please try again in {minutes} minutes",
      invalidEmail: "Invalid email format"
    },
    alreadyHaveAccount: "Already have an account?",
    signIn: "Sign in",
    agreeTermsStart: "By continuing, you agree to our",
    agreeTermsMiddle: "and",
    agreeTermsEnd: ".",
    termsOfService: "Terms of Service",
    privacyPolicy: "Privacy Policy"
  },
  onboarding: {
    profile: {
      title: "The pleasure is all ours...",
      description: "Tell us a bit about yourself to personalize your experience",
      avatarLabel: "Profile picture (optional)",
      avatarDescription: "Add your photo",
      firstNameLabel: "First name",
      firstNamePlaceholder: "John",
      lastNameLabel: "Last name",
      lastNamePlaceholder: "Doe",
      functionLabel: "Job Title/Role",
      functionPlaceholder: "e.g. Developer, Manager, Designer...",
      avatarSizeError: "Image is too large. Maximum size: 5MB",
      avatarUploadError: "Error uploading image. Please try again."
    },
    welcome: {
      title: "Welcome to Prodgy!",
      description: "Let's set up your workspace in just a few steps",
      step1Title: "Create your account",
      step1Description: "You've already completed this step",
      step2Title: "Set up your organization",
      step2Description: "Create your workspace to start using Prodgy"
    },
    organization: {
      title: "More Context, Less Effort",
      description: "From now on, you're already starting to provide maximum context for your AI agents about your business!",
      nameLabel: "Organization name",
      namePlaceholder: "My Company",
      nameDescription: "This is the name your team will see",
      industryLabel: "Industry/Sector",
      industryPlaceholder: "e.g. Technology, Healthcare, Education...",
      industryDescription: "What industry does your organization operate in?",
      sizeLabel: "Company size",
      sizePlaceholder: "Select size",
      sizes: {
        "1-10": "1-10 employees",
        "11-50": "11-50 employees",
        "51-200": "51-200 employees",
        "201-500": "201-500 employees",
        "501-1000": "501-1000 employees",
        "1001-5000": "1001-5000 employees",
        "5000+": "5000+ employees"
      },
      stageLabel: "Company stage",
      stagePlaceholder: "Select stage",
      stages: {
        idea: "Idea/Concept",
        startup: "Startup",
        growth: "Growth",
        established: "Established",
        enterprise: "Enterprise"
      },
      typeLabel: "Organization type",
      typePlaceholder: "Select a type",
      typeDescription: "Choose the type that best describes your organization",
      types: {
        personal: "Personal - For individual use",
        business: "Business - For commercial use"
      },
      infoLabel: "Description",
      infoPlaceholder: "Tell us about your organization...",
      infoDescription: "A brief description of your organization",
      websiteLabel: "Website",
      websitePlaceholder: "https://www.example.com",
      websiteDescription: "Your organization's website address",
      websiteHelper: "You can enter with or without https:// (e.g., example.com)",
      logoUpload: "Click to add a logo",
      logoDescription: "PNG, JPG or SVG (recommended: 512x512px).",
      logoSelected: "Logo selected.",
      logoSizeError: "Image is too large. Maximum size: 5MB.",
      logoUploadError: "Error uploading image. Please try again.",
      magicFillTooltip: "Fill with AI.",
      testingPrompt: "Just testing and out of ideas? Use the magic button to fill or complete the form with AI.",
      magicFillIntroTitle: "Your magic autofill assistant!",
      magicFillIntroBody: "This is the AI-powered autofill button. Throughout Prodgy it will always be around, ready to speed up your forms with contextual suggestions whenever you need."
    },
    steps: {
      profile: "Profile",
      survey: "Information",
      organization: "Organization",
      progress: "Step {current} of {total}"
    },
    additionalInfo: {
      title: "Help us tailor your experience!",
      description: "Additional information helps us better understand your needs",
      subtitle: "This helps us personalize your experience",
      optional: "(optional)",
      howFoundUs: {
        label: "How did you find Prodgy?",
        placeholder: "Select an option",
        options: {
          google: "Google/Search",
          linkedin: "LinkedIn",
          referral: "Colleague referral",
          social_media: "Social media",
          blog: "Blog/Article",
          ad: "Advertisement",
          event: "Event/Conference",
          email: "Email marketing",
          other: "Other"
        }
      },
      industry: {
        label: "What is your industry?",
        placeholder: "Select your industry",
        options: {
          technology: "Technology/Software",
          financial: "Financial/Fintech",
          healthcare: "Healthcare",
          ecommerce: "E-commerce/Retail",
          education: "Education",
          manufacturing: "Manufacturing/Industry",
          marketing: "Marketing/Agencies",
          legal: "Legal",
          construction: "Construction",
          food: "Food/Restaurants",
          services: "Services/Consulting",
          other: "Other"
        }
      },
      currentTools: {
        label: "What tools do you currently use?",
        placeholder: "Select tools",
        description: "Select all that apply",
        options: {
          trello: "Trello",
          asana: "Asana",
          monday: "Monday.com",
          jira: "Jira",
          notion: "Notion",
          clickup: "ClickUp",
          excel: "Excel/Google Sheets",
          slack: "Slack",
          email: "Email/Gmail",
          zapier: "Zapier/Make",
          powerbi: "Power BI",
          teams: "Microsoft Teams",
          other: "Other tools"
        }
      },
      mainGoal: {
        label: "What is your main goal with Prodgy?",
        placeholder: "e.g., Automate repetitive processes, improve team collaboration, centralize information...",
        description: "Tell us briefly what you hope to achieve"
      }
    },
    buttons: {
      back: "Back",
      continue: "Next step →",
      finish: "Finish"
    },
    validation: {
      requiredField: "The field {{field}} is required",
      requiredFieldsError: "* There are required fields that are not filled in.",
      firstNameMaxLength: "First name can be at most 50 characters",
      lastNameMinLength: "Last name must be at least 2 characters",
      lastNameMaxLength: "Last name can be at most 50 characters",
      functionMinLength: "Job title/role must be at least 2 characters",
      functionMaxLength: "Job title/role can be at most 100 characters",
      nameMinLength: "The name must be at least 2 characters",
      nameMaxLength: "The name can be at most 100 characters",
      typeRequired: "Select an organization type",
      infoRequired: "Organization description is required",
      websiteRequired: "Organization website is required",
      websiteInvalid: "Please enter a valid URL (e.g. https://example.com)",
      industryMinLength: "The industry must be at least 2 characters",
      industryMaxLength: "The industry can be at most 100 characters",
      sizeRequired: "Select company size",
      stageRequired: "Select company stage"
    },
    errors: {
      title: "Error",
      generic: "An error occurred. Please try again.",
      createFailed: "Failed to create organization. Please try again.",
      subscriptionFailed: "Unable to create subscription. Please try again or contact support.",
      paymentCheckFailed: "Unable to verify payment status. Please try again or contact support.",
      invalidEmail: "Invalid email format",
      userExists: "An account with this email already exists",
      rateLimitExceeded: "Too many attempts. Please try again in {minutes} minutes"
    },
    autoFinish: {
      finishing: "Finishing your setup...",
      errorDescription: "We couldn't finish your onboarding. Check your connection and try again.",
      retryButton: "Try again"
    }
  },
  waitlist: {
    title: "Trial Access Restricted",
    description: "Your user is not registered in our early access list for the Trial plan.",
    whatHappensTitle: "What happens now?",
    step1: "You will be registered on the waiting list",
    step2: "You will receive an email when access is granted",
    step3: "You can access Prodgy as soon as you are approved",
    requestButton: "Join Waiting List",
    footer: "You will be logged out and notified by email when access is available."
  },
  trial: {
    success: {
      title: "🎉 Trial Activated!",
      description: "Welcome! Your trial period has been successfully activated.",
      validUntil: "Trial valid until",
      creditsAvailable: "Available credits",
      message: "Enjoy all Prodgy features during the trial period!",
      startButton: "Let's Start!"
    }
  },
  unsubscribe: {
    loading: {
      title: "Processing your request...",
      description: "Please wait while we unsubscribe you from our mailing list"
    },
    success: {
      title: "Successfully Unsubscribed",
      description: "You will no longer receive emails related to this invite.",
      message: "We're sorry to see you go. If you changed your mind and would like to join our platform, we'd be happy to have you!",
      button: "Go to Sign In"
    },
    notFound: {
      title: "Invite Not Found",
      description: "This invite has already been removed or the link is incorrect.",
      message: "The unsubscribe link may have already been used or is no longer valid."
    },
    error: {
      title: "Error Processing Request",
      description: "An error occurred while processing your request. Please try again later.",
      message: "If the problem persists, please contact our support team."
    }
  },
  oauth: {
    callback: {
      loading: "Authenticating...",
      validating: "Validating your credentials...",
      error: "Authentication Error",
      errorDescription: "Unable to complete authentication. Please try again."
    },
    consent: {
      title: "Authorize application",
      description: "{{clientName}} wants to access your Prodgy workspace.",
      requestedPermissions: "Requested permissions",
      organization: "Organization",
      workspace: "Workspace",
      selectOrganization: "Select an organization",
      selectWorkspace: "Select a workspace",
      authorize: "Authorize",
      cancel: "Cancel",
      disclaimer: "You can revoke this access later. The issued token will stay restricted to the organization, workspace, and permissions selected on this screen.",
      scopeLabels: {
        "mcp_read": "Read your Prodgy data (MCP)",
        "mcp_write": "Create and edit your data (MCP)",
        "mcp_execute": "Execute platform agents and actions (MCP)",
        "mcp_admin": "Manage advanced settings (MCP)",
        "api_read": "Read your Prodgy data (API)",
        "api_write": "Create and edit your data (API)",
        "api_execute": "Execute workflows, agents and chat (API)",
        "api_admin": "Delete resources and manage integrations (API)"
      },
      errors: {
        invalidRequest: "Invalid or expired authorization request.",
        loadFailed: "Unable to load the authorization request.",
        submitFailed: "Unable to complete the authorization.",
        redirectFailed: "Unable to redirect back to the client application."
      }
    },
    error: {
      title: "Authentication Error",
      accessDenied: "Authentication cancelled. Please try again.",
      userCancelled: "Authentication cancelled. Please try again.",
      authFailed: "Unable to complete authentication. Please try again.",
      genericError: "An error occurred during authentication. Please try again.",
      emailMismatch: "The social account email does not match the invitation email. Please use the correct account.",
      inviteExpired: "The invitation link has expired. Please request a new invite.",
      inviteInvalid: "The invitation link is invalid or has already been used.",
      pendingInvite: "You have a pending invitation for this email. Please check your inbox and accept the invite to continue.",
      backToLogin: "Back to Sign In",
      tryAgain: "Try Again"
    },
    connecting: "Connecting..."
  }
} as const;
